import { Component } from 'preact';
import { connect } from 'unistore/preact';
import { route } from 'preact-router';
import { Text } from 'preact-i18n';
import get from 'get-value';

import ExternalIntegrationPage from '../ExternalIntegrationPage';
import SupervisionCard from './SupervisionCard';
import { RequestStatus } from '../../../../../utils/consts';
import createActionsExternalIntegrationUpdates from '../../../../../actions/externalIntegrationUpdates';
import { WEBSOCKET_MESSAGE_TYPES } from '../../../../../../../server/utils/constants';
import style from './style.css';

class ExternalIntegrationSupervisionPage extends Component {
  loadData = async () => {
    // loadData also runs when the user navigates to another integration: the
    // feedback of the previous one must not be shown on the new card
    this.setState({
      loadStatus: RequestStatus.Getting,
      actionStatus: null,
      actionError: null,
      updateResult: null,
      // unknown until the AI provider selection is read: the status of the
      // previous integration must not leak onto this one
      isActiveAiProvider: null,
      aiProviderReadFailed: false
    });
    const { selector } = this.props;
    try {
      const integration = await this.props.httpClient.get(`/api/v1/external_integration/${selector}`);
      if (selector !== this.props.selector) {
        // a newer request has started since, discard this stale result
        return;
      }
      this.setState({ integration, loadStatus: RequestStatus.Success });
      if (get(integration, 'manifest.type') === 'ai') {
        await this.loadAiProvider(selector);
      }
    } catch (e) {
      console.error(e);
      if (selector !== this.props.selector) {
        return;
      }
      this.setState({ integration: null, loadStatus: RequestStatus.Error });
    }
  };

  // whether this AI provider integration is the one the assistant uses:
  // uninstalling it switches the assistant back to Gladys Plus, the
  // confirmation must say so (capabilities/ai-type.md). true / false once
  // read, null while unknown — pending, or failed (aiProviderReadFailed): a
  // failed read is not a "not the provider" answer, the confirmation says
  // it is checking, then that it could not check
  loadAiProvider = async selector => {
    this.aiProviderRequest = (this.aiProviderRequest || 0) + 1;
    const request = this.aiProviderRequest;
    const isStale = () => selector !== this.props.selector || request !== this.aiProviderRequest;
    try {
      const aiProvider = await this.props.httpClient.get('/api/v1/ai_provider');
      if (isStale()) {
        // a newer read has started since, discard this stale answer
        return;
      }
      this.setState({ isActiveAiProvider: aiProvider.selector === selector, aiProviderReadFailed: false });
    } catch (e) {
      console.error(e);
      if (!isStale()) {
        this.setState({ aiProviderReadFailed: true });
      }
    }
  };

  isAiProvider = () => get(this.state, 'integration.manifest.type') === 'ai';

  executeAction = async action => {
    const previousVersion = this.state.integration && this.state.integration.version;
    this.setState({ actionStatus: RequestStatus.Getting, actionError: null, updateResult: null });
    try {
      const integration = await this.props.httpClient.post(
        `/api/v1/external_integration/${this.props.selector}/${action}`
      );
      // an update that changes nothing is a legitimate outcome (already on
      // the latest version): without this the button looks like it did
      // nothing at all
      const updateResult =
        action === 'update'
          ? { version: integration.version, upToDate: integration.version === previousVersion }
          : null;
      this.setState({ integration, actionStatus: RequestStatus.Success, updateResult });
      if (action === 'update') {
        // the header counter must drop right away, the user just did the
        // update it was asking for
        this.props.refreshExternalIntegrationsToUpdate();
      }
    } catch (e) {
      console.error(e);
      this.setState({ actionStatus: RequestStatus.Error, actionError: action, updateResult: null });
    }
  };

  askUninstall = () => {
    this.setState({ askingUninstall: true });
    if (this.isAiProvider()) {
      // the selection can change while this page is open (another tab, the
      // configuration screen): read it again now, the confirmation is where
      // the answer matters, and say "unknown" until it comes back
      this.setState({ isActiveAiProvider: null, aiProviderReadFailed: false });
      this.loadAiProvider(this.props.selector);
    }
  };

  cancelUninstall = () => {
    this.setState({ askingUninstall: false });
  };

  uninstall = async () => {
    this.setState({ uninstallStatus: RequestStatus.Getting });
    try {
      await this.props.httpClient.delete(`/api/v1/external_integration/${this.props.selector}`);
      route('/dashboard/integration/device');
    } catch (e) {
      console.error(e);
      this.setState({ uninstallStatus: RequestStatus.Error });
    }
  };

  onStatusChanged = payload => {
    if (payload && this.state.integration && payload.selector === this.props.selector) {
      this.setState({
        integration: Object.assign({}, this.state.integration, { status: payload.status })
      });
    }
  };

  onConnectionStatusUpdated = payload => {
    if (payload && this.state.integration && payload.selector === this.props.selector) {
      this.setState({
        integration: Object.assign({}, this.state.integration, {
          connection_status: { connected: payload.connected, message: payload.message }
        })
      });
    }
  };

  componentWillMount() {
    this.props.session.dispatcher.addListener(
      WEBSOCKET_MESSAGE_TYPES.EXTERNAL_INTEGRATION.STATUS_CHANGED,
      this.onStatusChanged
    );
    this.props.session.dispatcher.addListener(
      WEBSOCKET_MESSAGE_TYPES.EXTERNAL_INTEGRATION.CONNECTION_STATUS_UPDATED,
      this.onConnectionStatusUpdated
    );
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selector !== this.props.selector) {
      this.loadData();
    }
  }

  componentWillUnmount() {
    this.props.session.dispatcher.removeListener(
      WEBSOCKET_MESSAGE_TYPES.EXTERNAL_INTEGRATION.STATUS_CHANGED,
      this.onStatusChanged
    );
    this.props.session.dispatcher.removeListener(
      WEBSOCKET_MESSAGE_TYPES.EXTERNAL_INTEGRATION.CONNECTION_STATUS_UPDATED,
      this.onConnectionStatusUpdated
    );
  }

  render(props, state) {
    const language = (props.user && props.user.language) || 'en';
    return (
      <ExternalIntegrationPage selector={props.selector} integration={state.integration}>
        {state.loadStatus === RequestStatus.Error && (
          <div class="alert alert-danger">
            <Text id="integration.externalIntegration.supervision.loadError" />
          </div>
        )}
        {state.integration ? (
          <SupervisionCard
            integration={state.integration}
            language={language}
            actionStatus={state.actionStatus}
            actionError={state.actionError}
            updateResult={state.updateResult}
            uninstallStatus={state.uninstallStatus}
            askingUninstall={state.askingUninstall}
            isAiProvider={this.isAiProvider()}
            isActiveAiProvider={state.isActiveAiProvider}
            aiProviderReadFailed={state.aiProviderReadFailed}
            executeAction={this.executeAction}
            onAskUninstall={this.askUninstall}
            onCancelUninstall={this.cancelUninstall}
            onUninstall={this.uninstall}
          />
        ) : (
          state.loadStatus === RequestStatus.Getting && (
            <div class="card">
              <div class="card-body">
                <div class="dimmer active">
                  <div class="loader" />
                  <div class={`dimmer-content ${style.loadingPlaceholder}`} />
                </div>
              </div>
            </div>
          )
        )}
      </ExternalIntegrationPage>
    );
  }
}

export default connect(
  'user,session,httpClient',
  createActionsExternalIntegrationUpdates
)(ExternalIntegrationSupervisionPage);
